import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import jsPDF from 'jspdf';
import './SchoolDashboard.css';

function StatementsPreview() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  
  // Selectors
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'class-wise'
  
  // Statements (Invoices)
  const [statements, setStatements] = useState([]);
  const [loadingStatements, setLoadingStatements] = useState(false);
  const [schoolLogo, setSchoolLogo] = useState(null);
  const [statementSettings, setStatementSettings] = useState(null);
  const [showPublishSuccessPopup, setShowPublishSuccessPopup] = useState(false);
  const [publishedStatement, setPublishedStatement] = useState(null);

  // Helper function to get base class name without section
  const getClassDisplayName = (className) => {
    if (!className) return 'No Class';
    if (className.includes(' - ')) {
      return className.split(' - ')[0].trim();
    }
    return className.trim();
  };

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    return parseFloat(num || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (!storedSchoolData) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(storedSchoolData));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchAcademicYears();
      fetchTerms();
      fetchClasses();
      fetchSchoolLogo();
      fetchStatementSettings();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    if (schoolData?.id && selectedAcademicYear) {
      fetchStatements();
    }
  }, [schoolData?.id, selectedBranchId, selectedAcademicYear, selectedTerm, selectedClass, viewMode]);

  const fetchAcademicYears = async () => {
    try {
      const { data, error } = await supabase
        .from('academic_years')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('start_date', { ascending: false });

      if (error) {
        console.error('Error fetching academic years:', error);
        return;
      }

      setAcademicYears(data || []);
      if (data && data.length > 0 && !selectedAcademicYear) {
        setSelectedAcademicYear(data[0].id);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchTerms = async () => {
    try {
      const { data, error } = await supabase
        .from('terms')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('start_date', { ascending: true });

      if (error) {
        console.error('Error fetching terms:', error);
        return;
      }

      setTerms(data || []);
      if (data && data.length > 0 && !selectedTerm) {
        setSelectedTerm(data[0].id);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('class_name', { ascending: true });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setClasses(data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchSchoolLogo = async () => {
    if (!schoolData?.logo_url) return;
    
    try {
      const { data, error } = await supabase.storage
        .from('school-logos')
        .download(schoolData.logo_url);
      
      if (error) throw error;
      
      const url = URL.createObjectURL(data);
      setSchoolLogo(url);
    } catch (error) {
      console.error('Error fetching school logo:', error);
    }
  };

  const fetchStatementSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('statement_settings')
        .select('*')
        .eq('school_id', schoolData.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching statement settings:', error);
        return;
      }

      setStatementSettings(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchStatements = async () => {
    if (!selectedAcademicYear) return;

    try {
      setLoadingStatements(true);
      let query = supabase
        .from('student_invoices')
        .select(`
          *,
          students:student_id (
            id,
            first_name,
            middle_name,
            last_name,
            student_id_no,
            class_id,
            classes:class_id (id, class_name)
          )
        `)
        .eq('school_id', schoolData.id)
        .eq('academic_year_id', selectedAcademicYear)
        .order('generated_at', { ascending: false });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      if (selectedTerm) {
        const termName = terms.find(t => t.id === parseInt(selectedTerm))?.name || selectedTerm;
        query = query.eq('term', termName);
      }

      // Class filter
      if (viewMode === 'class-wise' && selectedClass) {
        // Filter by students in the selected class
        const { data: classStudents } = await supabase
          .from('students')
          .select('id')
          .eq('class_id', selectedClass)
          .eq('is_active', true);
        
        if (classStudents && classStudents.length > 0) {
          const studentIds = classStudents.map(s => s.id);
          query = query.in('student_id', studentIds);
        } else {
          setStatements([]);
          setLoadingStatements(false);
          return;
        }
      }

      const { data: invoicesData, error } = await query;

      if (error) {
        console.error('Error fetching statements:', error);
        setStatements([]);
        return;
      }

      // Fetch invoice items for each invoice
      if (invoicesData && invoicesData.length > 0) {
        // Get all unique class IDs from students
        const classIds = [...new Set(invoicesData
          .map(inv => inv.students?.class_id)
          .filter(Boolean))];
        
        // Fetch class fee mappings for these classes
        let mappingsQuery = supabase
          .from('class_fee_mapping')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('academic_year_id', selectedAcademicYear)
          .eq('status', 'published')
          .in('class_id', classIds);

        if (selectedBranchId) {
          mappingsQuery = mappingsQuery.or(`branch_id.eq.${selectedBranchId},branch_id.is.null`);
        } else {
          mappingsQuery = mappingsQuery.is('branch_id', null);
        }

        const { data: feeMappings } = await mappingsQuery;

        // Also try to fetch invoice_items as backup
        const invoiceIds = invoicesData.map(inv => inv.id);
        const { data: itemsData } = await supabase
          .from('invoice_items')
          .select('*')
          .in('invoice_id', invoiceIds);

        // Map items to invoices - use invoice_items if available, otherwise generate from Class Fee Mapping
        const statementsWithItems = invoicesData.map(invoice => {
          const student = invoice.students;
          let items = [];

          // First, try to use invoice_items from database
          if (itemsData && itemsData.length > 0) {
            items = itemsData.filter(item => parseInt(item.invoice_id) === parseInt(invoice.id));
          }

          // If no invoice_items found, generate from Class Fee Mapping
          if (items.length === 0 && student && student.class_id && feeMappings) {
            const classMappings = feeMappings.filter(mapping => 
              parseInt(mapping.class_id) === parseInt(student.class_id)
            );

            // Check if mapping applies to this student
            classMappings.forEach(mapping => {
              let appliesToStudent = false;
              
              if (mapping.applies_to === 'All') {
                appliesToStudent = true;
              } else if (mapping.applies_to === 'New') {
                if (student.admission_date) {
                  const admissionDate = new Date(student.admission_date);
                  const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));
                  if (academicYear && academicYear.start_date) {
                    const yearStart = new Date(academicYear.start_date);
                    appliesToStudent = admissionDate >= yearStart;
                  } else {
                    appliesToStudent = true;
                  }
                } else {
                  appliesToStudent = true;
                }
              } else if (mapping.applies_to === 'Returning') {
                if (student.admission_date) {
                  const admissionDate = new Date(student.admission_date);
                  const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));
                  if (academicYear && academicYear.start_date) {
                    const yearStart = new Date(academicYear.start_date);
                    appliesToStudent = admissionDate < yearStart;
                  } else {
                    appliesToStudent = true;
                  }
                } else {
                  appliesToStudent = true;
                }
              }

              if (appliesToStudent) {
                items.push({
                  id: `temp-${mapping.id}`,
                  invoice_id: invoice.id,
                  fee_name: mapping.fee_name,
                  amount: parseFloat(mapping.amount || 0),
                  discount: 0,
                  balance: parseFloat(mapping.amount || 0)
                });
              }
            });
          }

          return {
            ...invoice,
            invoice_items: items
          };
        });

        setStatements(statementsWithItems);
      } else {
        setStatements([]);
      }
    } catch (error) {
      console.error('Error:', error);
      setStatements([]);
    } finally {
      setLoadingStatements(false);
    }
  };

  const generateStatementPDF = (statement) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;

    const student = statement.students;
    const studentName = `${student?.first_name || ''} ${student?.middle_name || ''} ${student?.last_name || ''}`.trim();
    const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));
    const termName = terms.find(t => t.id === parseInt(selectedTerm))?.name || statement.term || 'N/A';

    // School Logo (if available)
    if (schoolLogo) {
      try {
        const img = new Image();
        img.src = schoolLogo;
        doc.addImage(img, 'PNG', margin, yPos, 40, 20);
        yPos += 25;
      } catch (error) {
        console.error('Error adding logo to PDF:', error);
      }
    }

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('FEE STATEMENT', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // School Information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(schoolData.school_name || 'School Name', margin, yPos);
    yPos += 6;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    if (schoolData.address) {
      doc.text(schoolData.address, margin, yPos);
      yPos += 5;
    }
    if (schoolData.phone) {
      doc.text(`Phone: ${schoolData.phone}`, margin, yPos);
      yPos += 5;
    }
    if (schoolData.email) {
      doc.text(`Email: ${schoolData.email}`, margin, yPos);
      yPos += 5;
    }
    yPos += 5;

    // Statement Details
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`Invoice Number: ${statement.invoice_number}`, pageWidth - margin, margin + 5, { align: 'right' });
    doc.text(`Date: ${new Date(statement.generated_at).toLocaleDateString()}`, pageWidth - margin, margin + 10, { align: 'right' });
    
    if (academicYear) {
      doc.text(`Academic Year: ${academicYear.year_name || 'N/A'}`, pageWidth - margin, margin + 15, { align: 'right' });
    }
    if (termName) {
      doc.text(`Term: ${termName}`, pageWidth - margin, margin + 20, { align: 'right' });
    }
    yPos = margin + 30;

    // Student Information
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Student Details:', margin, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Student Name: ${studentName}`, margin, yPos);
    yPos += 6;
    
    if (student?.student_id_no) {
      doc.text(`Admission No: ${student.student_id_no}`, margin, yPos);
      yPos += 6;
    }
    
    if (student?.classes?.class_name) {
      doc.text(`Class: ${getClassDisplayName(student.classes.class_name)}`, margin, yPos);
      yPos += 6;
    }
    yPos += 5;

    // Line
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Fee Breakdown
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Fee Breakdown:', margin, yPos);
    yPos += 8;

    // Table Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Fee Name', margin + 5, yPos);
    doc.text('Amount', pageWidth - margin - 60, yPos, { align: 'right' });
    doc.text('Discount', pageWidth - margin - 30, yPos, { align: 'right' });
    doc.text('Balance', pageWidth - margin - 5, yPos, { align: 'right' });
    yPos += 6;

    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;

    // Fee Items
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    if (statement.invoice_items && statement.invoice_items.length > 0) {
      statement.invoice_items.forEach((item) => {
        if (yPos > doc.internal.pageSize.getHeight() - 40) {
          doc.addPage();
          yPos = margin;
        }
        
        doc.text(item.fee_name || 'N/A', margin + 5, yPos);
        doc.text(`Rs ${formatNumber(item.amount)}`, pageWidth - margin - 60, yPos, { align: 'right' });
        doc.text(`Rs ${formatNumber(item.discount || 0)}`, pageWidth - margin - 30, yPos, { align: 'right' });
        doc.text(`Rs ${formatNumber(item.balance || item.amount - (item.discount || 0))}`, pageWidth - margin - 5, yPos, { align: 'right' });
        yPos += 6;
      });
    } else {
      doc.text('No fee items found.', margin + 5, yPos);
      yPos += 6;
    }

    yPos += 5;
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Total
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    const totalAmount = parseFloat(statement.total_amount || 0);
    const totalDiscount = parseFloat(statement.total_discount || 0);
    const totalBalance = parseFloat(statement.total_balance || 0);
    
    doc.text('Total Amount:', pageWidth - margin - 60, yPos, { align: 'right' });
    doc.text(`Rs ${formatNumber(totalAmount)}`, pageWidth - margin - 5, yPos, { align: 'right' });
    yPos += 6;

    if (totalDiscount > 0) {
      doc.text('Total Discount:', pageWidth - margin - 60, yPos, { align: 'right' });
      doc.text(`Rs ${formatNumber(totalDiscount)}`, pageWidth - margin - 5, yPos, { align: 'right' });
      yPos += 6;
    }

    doc.setFontSize(12);
    doc.text('Total Due:', pageWidth - margin - 60, yPos, { align: 'right' });
    doc.text(`Rs ${formatNumber(totalBalance)}`, pageWidth - margin - 5, yPos, { align: 'right' });
    yPos += 10;

    // Status
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    const status = statement.status || 'generated';
    const statusColor = status === 'paid' ? '#10B981' : status === 'partial' ? '#F59E0B' : '#EF4444';
    doc.setTextColor(statusColor);
    doc.text(`Status: ${status.toUpperCase()}`, margin, yPos);
    doc.setTextColor(0, 0, 0); // Reset to black
    yPos += 8;

    // Footer
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;

    // Payment Options
    if (statementSettings) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Payment Options', margin, yPos);
      yPos += 6;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      if (statementSettings.payment_option_1) {
        doc.text(`1) ${statementSettings.payment_option_1}`, margin + 5, yPos);
        yPos += 6;
      }
      if (statementSettings.payment_option_2) {
        doc.text(`2) ${statementSettings.payment_option_2}`, margin + 5, yPos);
        yPos += 6;
      }

      yPos += 3;
      doc.setLineWidth(0.3);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 6;

      // Contact Message
      if (statementSettings.contact_message) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        const contactLines = doc.splitTextToSize(statementSettings.contact_message, pageWidth - 2 * margin);
        contactLines.forEach(line => {
          doc.text(line, margin, yPos);
          yPos += 4;
        });
        yPos += 3;
      }

      // Thank You Message
      if (statementSettings.thank_you_message) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        const thankYouLines = doc.splitTextToSize(statementSettings.thank_you_message, pageWidth - 2 * margin);
        thankYouLines.forEach(line => {
          doc.text(line, pageWidth / 2, yPos, { align: 'center' });
          yPos += 5;
        });
      }
    } else {
      // Default footer if settings not configured
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.text('Thank you!', pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      doc.text('This is a computer-generated statement.', pageWidth / 2, yPos, { align: 'center' });
    }

    // Save PDF
    const fileName = `Statement_${statement.invoice_number}_${studentName.replace(/\s+/g, '_')}.pdf`;
    doc.save(fileName);
  };

  const handlePrintStatement = (statement) => {
    const printWindow = window.open('', '_blank');
    const student = statement.students;
    const studentName = `${student?.first_name || ''} ${student?.middle_name || ''} ${student?.last_name || ''}`.trim();
    const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));
    const termName = terms.find(t => t.id === parseInt(selectedTerm))?.name || statement.term || 'N/A';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Fee Statement - ${statement.invoice_number}</title>
          <style>
            @media print {
              @page { margin: 20mm; }
            }
            body {
              font-family: Arial, sans-serif;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
            }
            .school-info {
              margin-bottom: 20px;
            }
            .statement-details {
              text-align: right;
              margin-bottom: 20px;
            }
            .student-info {
              margin-bottom: 20px;
            }
            .fee-breakdown {
              margin-bottom: 20px;
            }
            .fee-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            .fee-table th,
            .fee-table td {
              padding: 8px;
              text-align: left;
              border-bottom: 1px solid #ddd;
            }
            .fee-table th {
              background-color: #f9fafb;
              font-weight: bold;
            }
            .fee-table .amount {
              text-align: right;
            }
            .total {
              margin-top: 20px;
              text-align: right;
              font-weight: bold;
              font-size: 1.2em;
            }
            .status {
              margin-top: 10px;
              font-weight: bold;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-style: italic;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>FEE STATEMENT</h1>
          </div>
          
          <div class="school-info">
            <h2>${schoolData.school_name || 'School Name'}</h2>
            ${schoolData.address ? `<p>${schoolData.address}</p>` : ''}
            ${schoolData.phone ? `<p>Phone: ${schoolData.phone}</p>` : ''}
            ${schoolData.email ? `<p>Email: ${schoolData.email}</p>` : ''}
          </div>
          
          <div class="statement-details">
            <p><strong>Invoice Number:</strong> ${statement.invoice_number}</p>
            <p><strong>Date:</strong> ${new Date(statement.generated_at).toLocaleDateString()}</p>
            ${academicYear ? `<p><strong>Academic Year:</strong> ${academicYear.year_name || 'N/A'}</p>` : ''}
            ${termName ? `<p><strong>Term:</strong> ${termName}</p>` : ''}
          </div>
          
          <div class="student-info">
            <h3>Student Details:</h3>
            <p><strong>Student Name:</strong> ${studentName}</p>
            ${student?.student_id_no ? `<p><strong>Admission No:</strong> ${student.student_id_no}</p>` : ''}
            ${student?.classes?.class_name ? `<p><strong>Class:</strong> ${getClassDisplayName(student.classes.class_name)}</p>` : ''}
          </div>
          
          <div class="fee-breakdown">
            <h3>Fee Breakdown:</h3>
            <table class="fee-table">
              <thead>
                <tr>
                  <th>Fee Name</th>
                  <th class="amount">Amount</th>
                  <th class="amount">Discount</th>
                  <th class="amount">Balance</th>
                </tr>
              </thead>
              <tbody>
                ${statement.invoice_items && statement.invoice_items.length > 0
                  ? statement.invoice_items.map(item => `
                    <tr>
                      <td>${item.fee_name || 'N/A'}</td>
                      <td class="amount">Rs ${formatNumber(item.amount)}</td>
                      <td class="amount">Rs ${formatNumber(item.discount || 0)}</td>
                      <td class="amount">Rs ${formatNumber(item.balance || item.amount - (item.discount || 0))}</td>
                    </tr>
                  `).join('')
                  : '<tr><td colspan="4">No fee items found.</td></tr>'
                }
              </tbody>
            </table>
          </div>
          
          <div class="total">
            <p><strong>Total Amount:</strong> Rs ${formatNumber(statement.total_amount || 0)}</p>
            ${parseFloat(statement.total_discount || 0) > 0 
              ? `<p><strong>Total Discount:</strong> Rs ${formatNumber(statement.total_discount)}</p>` 
              : ''}
            <p><strong>Total Due:</strong> Rs ${formatNumber(statement.total_balance || 0)}</p>
          </div>
          
          <div class="status">
            <p>Status: <strong>${(statement.status || 'generated').toUpperCase()}</strong></p>
          </div>
          
          <div class="footer">
            ${statementSettings ? `
              <div style="margin-top: 20px; text-align: left;">
                <h4 style="margin-bottom: 10px;">Payment Options</h4>
                ${statementSettings.payment_option_1 ? `<p style="margin-left: 20px; margin-bottom: 5px;">1) ${statementSettings.payment_option_1}</p>` : ''}
                ${statementSettings.payment_option_2 ? `<p style="margin-left: 20px; margin-bottom: 5px;">2) ${statementSettings.payment_option_2}</p>` : ''}
              </div>
              ${statementSettings.contact_message ? `<p style="margin-top: 15px; color: #666;">${statementSettings.contact_message}</p>` : ''}
              ${statementSettings.thank_you_message ? `<p style="margin-top: 15px; text-align: center; font-weight: bold;">${statementSettings.thank_you_message}</p>` : ''}
            ` : `
              <p>Thank you!</p>
              <p>This is a computer-generated statement.</p>
            `}
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handlePublishToParentApp = async (statement) => {
    try {
      // Update statement status to 'sent'
      const { error } = await supabase
        .from('student_invoices')
        .update({ 
          status: 'sent',
          sent_at: new Date().toISOString()
        })
        .eq('id', statement.id);

      if (error) throw error;

      // Show success popup
      setPublishedStatement(statement);
      setShowPublishSuccessPopup(true);
      
      await fetchStatements();
    } catch (error) {
      console.error('Error publishing to parent app:', error);
      alert('Failed to publish. Please try again.');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="school-dashboard">
      {/* Publish Success Popup */}
      {showPublishSuccessPopup && publishedStatement && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '20px',
            padding: '2.5rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
            animation: 'slideInScale 0.4s ease',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative background elements */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background: 'linear-gradient(135deg, rgba(255, 140, 66, 0.1) 0%, rgba(230, 126, 34, 0.1) 100%)',
              borderRadius: '50%',
              zIndex: 0
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '150px',
              height: '150px',
              background: 'linear-gradient(135deg, rgba(255, 140, 66, 0.1) 0%, rgba(230, 126, 34, 0.1) 100%)',
              borderRadius: '50%',
              zIndex: 0
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Success Icon */}
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 1.5rem',
                background: 'linear-gradient(135deg, #FF8C42 0%, #E67E22 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'scaleIn 0.5s ease',
                boxShadow: '0 10px 30px rgba(255, 140, 66, 0.4)'
              }}>
                <svg
                  width="50"
                  height="50"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ animation: 'checkmarkDraw 0.6s ease 0.3s both' }}
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>

              {/* Success Message */}
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: '#1F2937',
                marginBottom: '1rem',
                animation: 'fadeInUp 0.5s ease 0.2s both'
              }}>
                Published Successfully!
              </h2>

              <p style={{
                fontSize: '1rem',
                color: '#6B7280',
                marginBottom: '1.5rem',
                animation: 'fadeInUp 0.5s ease 0.3s both'
              }}>
                The statement has been published to Parent APP.
              </p>

              {/* Statement Details */}
              {publishedStatement && (() => {
                const student = publishedStatement.students;
                const studentName = `${student?.first_name || ''} ${student?.middle_name || ''} ${student?.last_name || ''}`.trim();
                
                return (
                  <div style={{
                    background: '#F9FAFB',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '1.5rem',
                    textAlign: 'left',
                    animation: 'fadeInUp 0.5s ease 0.4s both'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                      <span style={{ color: '#6B7280', fontWeight: '500' }}>Invoice Number:</span>
                      <span style={{ color: '#1F2937', fontWeight: '600' }}>{publishedStatement.invoice_number}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                      <span style={{ color: '#6B7280', fontWeight: '500' }}>Student Name:</span>
                      <span style={{ color: '#1F2937', fontWeight: '600' }}>{studentName}</span>
                    </div>
                    {student?.student_id_no && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                        <span style={{ color: '#6B7280', fontWeight: '500' }}>Admission No:</span>
                        <span style={{ color: '#1F2937', fontWeight: '600' }}>{student.student_id_no}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6B7280', fontWeight: '500' }}>Total Due:</span>
                      <span style={{ color: '#FF8C42', fontWeight: '700', fontSize: '1.1rem' }}>
                        Rs {formatNumber(publishedStatement.total_balance || 0)}
                      </span>
                    </div>
                  </div>
                );
              })()}

              {/* Close Button */}
              <button
                onClick={() => {
                  setShowPublishSuccessPopup(false);
                  setPublishedStatement(null);
                }}
                style={{
                  padding: '0.75rem 2rem',
                  background: 'linear-gradient(135deg, #FF8C42 0%, #E67E22 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(255, 140, 66, 0.3)',
                  animation: 'fadeInUp 0.5s ease 0.5s both'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(255, 140, 66, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(255, 140, 66, 0.3)';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInScale {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes checkmarkDraw {
          from {
            stroke-dasharray: 0, 24;
            opacity: 0;
          }
          to {
            stroke-dasharray: 24, 24;
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      
      <SchoolSidebar schoolData={schoolData} />
      
      <div className="main-content" style={{ 
        width: '100%', 
        maxWidth: '100%', 
        overflowX: 'hidden',
        boxSizing: 'border-box',
        padding: '1.5rem'
      }}>
        <div className="content-header" style={{ 
          width: '100%', 
          maxWidth: '100%',
          boxSizing: 'border-box',
          marginBottom: '1rem'
        }}>
          <h1>Statements Preview</h1>
          <p>Preview and print fee statements</p>
        </div>

        {/* Selectors Row */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '1rem', 
          marginBottom: '1.5rem',
          alignItems: 'flex-end',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div className="form-group" style={{ minWidth: '200px', flex: '1' }}>
            <label htmlFor="academic_year">Academic Year *</label>
            <select
              id="academic_year"
              value={selectedAcademicYear}
              onChange={(e) => setSelectedAcademicYear(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="">Select Academic Year</option>
              {academicYears.map(year => (
                <option key={year.id} value={year.id}>
                  {year.year_name || `${new Date(year.start_date).getFullYear()}-${new Date(year.end_date).getFullYear()}`}
                </option>
              ))}
            </select>
          </div>

          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />

          <div className="form-group" style={{ minWidth: '150px', flex: '1' }}>
            <label htmlFor="term">Term</label>
            <select
              id="term"
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="">All Terms</option>
              {terms.map(term => (
                <option key={term.id} value={term.id}>{term.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* View Mode and Class Selector */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '1rem', 
          marginBottom: '2rem',
          alignItems: 'flex-end',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div className="form-group" style={{ minWidth: '150px' }}>
            <label>View Mode</label>
            <select
              value={viewMode}
              onChange={(e) => {
                setViewMode(e.target.value);
                if (e.target.value === 'all') {
                  setSelectedClass('');
                }
              }}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="all">All Classes</option>
              <option value="class-wise">Class-wise</option>
            </select>
          </div>

          {viewMode === 'class-wise' && (
            <div className="form-group" style={{ minWidth: '200px', flex: '1' }}>
              <label htmlFor="class">Class *</label>
              <select
                id="class"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {getClassDisplayName(cls.class_name)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Statements List */}
        <div className="dashboard-section" style={{ 
          width: '100%', 
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: '2rem'
        }}>
          {loadingStatements ? (
            <p style={{ textAlign: 'center', padding: '2rem' }}>Loading statements...</p>
          ) : statements.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6B7280' }}>
              <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>No statements found for the selected filters.</p>
              <p>Please select an academic year and check your filters.</p>
            </div>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
              gap: '1.5rem' 
            }}>
              {statements.map((statement) => {
                const student = statement.students;
                const studentName = `${student?.first_name || ''} ${student?.middle_name || ''} ${student?.last_name || ''}`.trim();
                const status = statement.status || 'generated';
                const statusColor = status === 'paid' ? '#10B981' : status === 'partial' ? '#F59E0B' : status === 'sent' ? '#3B82F6' : '#6B7280';
                
                return (
                  <div
                    key={statement.id}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e9ecef',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    {/* School Logo & Details */}
                    <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
                      {schoolLogo && (
                        <img 
                          src={schoolLogo} 
                          alt="School Logo" 
                          style={{ maxHeight: '60px', marginBottom: '0.5rem' }}
                        />
                      )}
                      <h3 style={{ margin: '0.5rem 0', color: '#1F2937' }}>
                        {schoolData.school_name || 'School Name'}
                      </h3>
                      {schoolData.address && (
                        <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: '0.25rem 0' }}>
                          {schoolData.address}
                        </p>
                      )}
                    </div>

                    {/* Statement Header */}
                    <div style={{ 
                      borderBottom: '2px solid #E5E7EB', 
                      paddingBottom: '1rem', 
                      marginBottom: '1rem' 
                    }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#1F2937' }}>Fee Statement</h4>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                        <span style={{ color: '#6B7280' }}>Invoice:</span>
                        <span style={{ fontWeight: '600' }}>{statement.invoice_number}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        <span style={{ color: '#6B7280' }}>Date:</span>
                        <span>{new Date(statement.generated_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {/* Student Details */}
                    <div style={{ marginBottom: '1rem' }}>
                      <h5 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '0.875rem' }}>Student Details</h5>
                      <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>
                        <strong>Name:</strong> {studentName}
                      </p>
                      {student?.student_id_no && (
                        <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>
                          <strong>Admission No:</strong> {student.student_id_no}
                        </p>
                      )}
                      {student?.classes?.class_name && (
                        <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>
                          <strong>Class:</strong> {getClassDisplayName(student.classes.class_name)}
                        </p>
                      )}
                    </div>

                    {/* Fee Breakdown Summary */}
                    <div style={{ 
                      background: '#F9FAFB', 
                      borderRadius: '8px', 
                      padding: '1rem', 
                      marginBottom: '1rem' 
                    }}>
                      <h5 style={{ margin: '0 0 0.75rem 0', color: '#374151', fontSize: '0.875rem' }}>Fee Summary</h5>
                      {statement.invoice_items && statement.invoice_items.length > 0 ? (
                        <div style={{ fontSize: '0.875rem' }}>
                          {statement.invoice_items.slice(0, 3).map((item, idx) => (
                            <div key={idx} style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              marginBottom: '0.5rem' 
                            }}>
                              <span>{item.fee_name || 'N/A'}</span>
                              <span style={{ fontWeight: '600' }}>
                                Rs {formatNumber(item.balance || item.amount - (item.discount || 0))}
                              </span>
                            </div>
                          ))}
                          {statement.invoice_items.length > 3 && (
                            <p style={{ margin: '0.5rem 0 0 0', color: '#6B7280', fontSize: '0.8rem' }}>
                              + {statement.invoice_items.length - 3} more items
                            </p>
                          )}
                        </div>
                      ) : (
                        <p style={{ margin: 0, color: '#6B7280', fontSize: '0.875rem' }}>No fee items</p>
                      )}
                    </div>

                    {/* Total Due */}
                    <div style={{ 
                      borderTop: '2px solid #E5E7EB', 
                      paddingTop: '1rem', 
                      marginBottom: '1rem' 
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1F2937' }}>Total Due:</span>
                        <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#10B981' }}>
                          Rs {formatNumber(statement.total_balance || 0)}
                        </span>
                      </div>
                      <div style={{ 
                        marginTop: '0.5rem', 
                        padding: '0.5rem', 
                        background: statusColor + '20', 
                        borderRadius: '6px',
                        textAlign: 'center'
                      }}>
                        <span style={{ 
                          fontSize: '0.875rem', 
                          fontWeight: '600', 
                          color: statusColor 
                        }}>
                          Status: {status.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '0.5rem', 
                      flexWrap: 'wrap',
                      borderTop: '1px solid #E5E7EB',
                      paddingTop: '1rem'
                    }}>
                      <button
                        onClick={() => generateStatementPDF(statement)}
                        style={{
                          flex: '1',
                          minWidth: '120px',
                          padding: '0.625rem 1rem',
                          background: '#10B981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#059669';
                          e.target.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#10B981';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        📥 Download PDF
                      </button>
                      <button
                        onClick={() => handlePrintStatement(statement)}
                        style={{
                          flex: '1',
                          minWidth: '120px',
                          padding: '0.625rem 1rem',
                          background: '#3B82F6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#2563EB';
                          e.target.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#3B82F6';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        🖨️ Print
                      </button>
                      <button
                        onClick={() => handlePublishToParentApp(statement)}
                        disabled={statement.status === 'sent'}
                        style={{
                          flex: '1',
                          minWidth: '120px',
                          padding: '0.625rem 1rem',
                          background: statement.status === 'sent' ? '#D1D5DB' : '#FF8C42',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          cursor: statement.status === 'sent' ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          opacity: statement.status === 'sent' ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (statement.status !== 'sent') {
                            e.target.style.background = '#E67E22';
                            e.target.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (statement.status !== 'sent') {
                            e.target.style.background = '#FF8C42';
                            e.target.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        {statement.status === 'sent' ? '✅ Published' : '📱 Publish to Parent APP'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StatementsPreview;
